import FuturePaymentOverview from './futurePaymentOverview';
import {
  colorPalette,
  getLabels,
  defaultContractCount,
  getMonthlyInstallment,
  getContractInstallment,
  getDueDateForGraph,
  T_MONTHS,
  formatRawtData,
  months,
  currentYear,
  currentMonth,
  CALENDAR_YEAR,
} from './graphConfig';

import {
  pushViewAction,
  setContractQueryAction,
  setMonthQueryAction,
} from '../../redux/actions/Actions';
import {appStore} from '../../redux/createStore';
import {formatCurrency, getCountryCode} from '../../utils/util';
import CurrentlyUnavailableCard from '../currently_unavailable_div/CurrentlyUnavailableCard';
import SkeletonLoader from '../skeletonLoader/SkeletonLoader';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import React from 'react';
import {Bar} from 'react-chartjs-2';
import {useTranslation} from 'react-i18next';
import {connect} from 'react-redux';
import './futurePaymentGraph.css';

ChartJS.register(
  ChartDataLabels,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);
const FuturePaymentGraph = (props) => {
  const ALLCONTRACT = 'ALL_CONTRACTS';
  const {t} = useTranslation();
  const [chartData, setChartData] = React.useState(null);
  const [scheduleInfo, setScheduleInfo] = React.useState(null);
  const [chartMode, selectChartMode] = React.useState(T_MONTHS);

  const isMobile = window.innerWidth <= 768;
  const additionalLegendHeight = {
    beforeInit(chart) {
      const originalFit = chart.legend.fit;
      chart.legend.fit = function fit() {
        originalFit.bind(chart.legend)();
        this.height += 50;
      };
    },
  };
  const options = {
    layout: {
      padding: {
        left: 0,
        right: 0,
        top: isMobile ? 0 : 50,
        bottom: 0,
      },
    },
    plugins: {
      legend: {
        display: props.contractCounts > defaultContractCount ? false : true,
        position: isMobile ? 'top' : 'right',
        onClick: (e, legendItem) => {
          const index = legendItem.datasetIndex;
          const updatedData = {
            ...chartData,
            datasets: chartData.datasets.map((dataset, i) => {
              if (i === index) {
                return {
                  ...dataset,
                  hidden: !dataset.hidden,
                };
              }
              return dataset;
            }),
          };
          setChartData(updatedData);
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem, data) {
            let tooltipTexts = [
              `${t('AMOUNT')}: ${formatCurrency(tooltipItem.raw)}`,
            ];
            if (props.contractCounts > defaultContractCount) {
              let peymentCount =
                tooltipItem.dataset.dataCounts[tooltipItem.dataIndex];
              tooltipTexts.push(`${t('NUMBER_OF_PAYMENTS')}: ${peymentCount}`);
            } else {
              let contractNo = tooltipItem.dataset.label;
              let month = tooltipItem.label.split(' ')[0];

              let year;
              const country_code = getCountryCode();
              if (chartMode === CALENDAR_YEAR) {
                year = currentYear;
              } else {
                year =
                  currentMonth <= months[country_code].indexOf(month)
                    ? currentYear
                    : currentYear + 1;
              }
              let paymentSchedule = props.paymentSchedule[contractNo][year];
              let paymentDueDate = getDueDateForGraph(
                paymentSchedule,
                months[country_code].indexOf(month)
              );
              tooltipTexts.push(`${t('CONTRACT_NO')}: ${contractNo}`);
              tooltipTexts.push(`${t('DUE_DATE')}: ${paymentDueDate}`);
            }
            return tooltipTexts;
          },
        },
      },
      datalabels: {
        color: 'black',
        rotation: -45,
        font: {
          weight: 200,
          size: 10,
        },
        anchor: 'end',
        align: 'end',
        formatter: (value, context) => {
          const dataset = context.chart.data.datasets;
          const dataIndex = context.dataIndex;
          let total = 0;
          let lastIndexWithoutHidden;
          for (let i = chartData.datasets.length - 1; i >= 0; i--) {
            if (!chartData.datasets[i].hidden) {
              lastIndexWithoutHidden = i;
              break;
            }
          }
          for (let i = 0; i <= context.datasetIndex; i++) {
            if (!dataset[i].hidden) {
              total += dataset[i].data[dataIndex];
            }
          }
          if (context.datasetIndex === lastIndexWithoutHidden) {
            return formatCurrency(total);
          } else {
            return '';
          }
        },
      },
    },
    // responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
      },
      y: {
        stacked: true,
        grid: {
          display: true,
          borderDash: [5, 5],
        },
      },
    },
    onClick: (event) => {
      if (event && event.chart.tooltip.dataPoints) {
        let currentMonth = event.chart.tooltip.dataPoints[0].label;
        handleRedirect(ALLCONTRACT, currentMonth);
      }
    },
  };
  const getOptions = () => {
    let formattedOption = {...options};
    props.contractCounts > defaultContractCount
      ? ((formattedOption.scales.x.stacked = false),
        (formattedOption.scales.y.stacked = false))
      : formattedOption;
    return formattedOption;
  };
  const createBarData = (records) => {
    let monthlyData = getMonthlyInstallment(records, chartMode);
    let datasets = [
      {
        data: Object.values(monthlyData.monthlyInstallment),
        backgroundColor: 'rgba(84, 88, 90, 1)',
        barPercentage: 0.1,
        categoryPercentage: 2,
        borderRadius: 10,
        dataCounts: Object.values(monthlyData.monthlyInstallmentCount),
      },
    ];
    return datasets;
  };
  const createStackBarData = (records) => {
    let datasets = [];
    Object.keys(records).forEach((key, i) => {
      let contractInstallments = getContractInstallment(
        records[key],
        chartMode
      );
      let stack = {
        label: key,
        data: contractInstallments,
        backgroundColor: colorPalette[i],
        barPercentage: 0.3,
        borderRadius: 7,
      };
      datasets.push(stack);
    });
    return datasets;
  };
  const createDataset = (records) => {
    let datasets;
    if (props.contractCounts > defaultContractCount) {
      datasets = createBarData(records);
    } else {
      datasets = createStackBarData(records);
    }
    const labels = getLabels(chartMode);
    const data = {labels, datasets};
    setChartData(data);
  };
  const handleRedirect = (queryContract, month = null) => {
    appStore.dispatch(setContractQueryAction(queryContract));
    appStore.dispatch(setMonthQueryAction(month));
    appStore.dispatch(pushViewAction('paymentSchedule'));
  };
  React.useEffect(() => {
    if (props.paymentSchedule) {
      const {formattedrawData, totalInstalmentCount, totalInstallmentAmount} =
        formatRawtData(props.paymentSchedule, chartMode);
      createDataset(formattedrawData);
      setScheduleInfo({
        totalAmount: totalInstallmentAmount,
        installmentCount: totalInstalmentCount,
      });
    }
  }, [props.paymentSchedule, chartMode]);
  return (
    <div className={'futurePaymentWrapper'}>
      <div className={'futurePaymentTitle'}>
        <span className="futurePaymentGraphTitle">{t('FUTURE_PAYMENTS')}</span>
        <span
          id="paymentScheduleLink"
          onClick={() => handleRedirect(ALLCONTRACT)}
          className={`${props.paymentScheduleError && 'disableLink'}`}
        >
          {t('VIEW_PAYMENT_SCHEDULE')}
        </span>
      </div>
      {props.paymentScheduleLoader ? (
        <SkeletonLoader componentName="futurePaymentGraph" />
      ) : props.paymentScheduleError ? (
        <CurrentlyUnavailableCard
          title={t('SOMETHING_WENT_WRONG')}
          subTitle={t('PAYMENT_SCHEDULE_NOT_AVAILABLE')}
          fullScreen={false}
        />
      ) : (
        <div
          className={'futurePaymentContainer'}
          data-testid="futurePaymentContainer"
        >
          <FuturePaymentOverview
            scheduleInfo={scheduleInfo}
            chartMode={chartMode}
            onControllerClick={(mode) => selectChartMode(mode)}
          />
          {chartData && scheduleInfo ? (
            <div className="barChartCard">
              <div className="barChartWrapper">
                <Bar
                  data-testid="abc"
                  options={getOptions()}
                  data={chartData}
                  plugins={[additionalLegendHeight]}
                />
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export const mapStateToProps = (state) => {
  return {
    paymentSchedule:
      state.appStates.paymentSchedule?.paymentSchedules?.installments,
    paymentScheduleLoader: state.appStates.paymentScheduleLoader,
    paymentScheduleError: state.appStates.paymentScheduleError,
  };
};
export default connect(mapStateToProps)(FuturePaymentGraph);
